import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  IconButton,
  MenuItem,
  Box,
  Menu,
  Typography,
  Dialog,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import PhoneIcon from "@mui/icons-material/Phone";
import { useState, useEffect, useRef } from "react";
import Switch from "@mui/material/Switch";
import store from "../../store";
import LogoutIcon from "@mui/icons-material/Logout";
import UpdateIcon from "@mui/icons-material/Update";
import LockIcon from "@mui/icons-material/Lock";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import SettingsIcon from "@mui/icons-material/Settings";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import "./style.scss";
import { EmailOutlined } from "@mui/icons-material";
import Alert from "@mui/material/Alert";
import axios from "axios";

function formatProjectLabel(description: string): string {
  if (!description) return description;

  const dashCount = (description.match(/-/g) || []).length;

  if (dashCount >= 3) {
    let dashesFound = 0;
    let sliceIndex = -1;
    for (let i = 0; i < description.length; i++) {
      if (description[i] === "-") {
        dashesFound++;
        if (dashesFound === 3) {
          sliceIndex = i;
          break;
        }
      }
    }
    const baseLabel = description.slice(0, sliceIndex).trimEnd();
    const match = baseLabel.trimEnd().match(/(\d+)$/);
    if (!match) return baseLabel;
    const trailingNumber = match[1];
    const lastDigit = parseInt(trailingNumber[trailingNumber.length - 1], 10);
    if (lastDigit === 0 && trailingNumber.length >= 2) {
      const lastTwo = parseInt(trailingNumber.slice(-2), 10);
      return `${baseLabel} - ${lastTwo} Days Ahead`;
    }
    return `${baseLabel} - ${lastDigit} Days Ahead`;
  }

  if (dashCount === 2) {
    const parts = description.split("-");
    const afterSecondDash = parts.slice(2).join("-").trim();
    if (!/^\d+$/.test(afterSecondDash) || afterSecondDash.length >= 5) {
      return description;
    }
    const lastDigit = parseInt(afterSecondDash[afterSecondDash.length - 1], 10);
    if (lastDigit === 0 && afterSecondDash.length >= 2) {
      const lastTwo = parseInt(afterSecondDash.slice(-2), 10);
      return `${description} - ${lastTwo} Days Ahead`;
    }
    return `${description} - ${lastDigit} Days Ahead`;
  }

  return description;
}

const Header = (props: any) => {
  const mystyle = {
    arrowIcon: {
      color: "white",
    },
    header: {
      backgroundColor: "black",
      padding: "10px 20px",
      color: "white",
    },
    content: {
      padding: "0 20px",
    },
    textBox: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    projectLocation: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginLeft: "16px",
      color: "white",
      fontSize: "16px",
      fontWeight: 500,
    },
  };

  const location = useLocation();
  const navigate = useNavigate();
  const data = useSelector((state: any) => state?.app);

  const isSSO = localStorage.getItem("sso_multi_client") === "true";
  const userId = localStorage.getItem("user_id");
  const forecastId = localStorage.getItem("fid");

  const [projectLocation, setProjectLocation] = useState<string>(() => {
    const raw =
      localStorage.getItem("project_location") ||
      localStorage.getItem("location") ||
      localStorage.getItem("project") ||
      localStorage.getItem("operation") ||
      "";
    return formatProjectLabel(raw);
  });

  const handleToggle = () => {
    store.dispatch({
      type: "TOGGLE_MENU",
      payload: data.toggle ? false : true,
    });
  };

  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [sanchorElUser, setsAnchorElUser] = useState<null | HTMLElement>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [updateIcon, setUpdateIcon] = useState(null);
  const [updateStatus, setUpdateStatus] = useState<any>(null);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const popup = useRef<HTMLDivElement>(null);

  const [userModal, setUserModal] = useState<any | {}>({
    name: localStorage.getItem("user_name") || "",
    id: localStorage.getItem("user_id") || "",
    email: localStorage.getItem("email_address") || "",
  });

  const [passwordModal, setPasswordModal] = useState<any | {}>({
    id: localStorage.getItem("user_id"),
    old_password: "",
    password: "",
  });

  useEffect(() => {
    const handleLocationUpdate = () => {
      const raw =
        localStorage.getItem("project_location") ||
        localStorage.getItem("location") ||
        localStorage.getItem("project") ||
        localStorage.getItem("operation") ||
        "";
      setProjectLocation(formatProjectLabel(raw));
    };

    window.addEventListener("storage", handleLocationUpdate);
    window.addEventListener("localStorageUpdate", handleLocationUpdate);
    window.addEventListener("locationUpdated", handleLocationUpdate);

    return () => {
      window.removeEventListener("storage", handleLocationUpdate);
      window.removeEventListener("localStorageUpdate", handleLocationUpdate);
      window.removeEventListener("locationUpdated", handleLocationUpdate);
    };
  }, []);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleOpenUserMenus = (
    event: React.MouseEvent<HTMLElement>,
    option: any
  ) => {
    if (option === "Settings") {
      if (isSSO) {
        // console.log("Settings not available for SSO users");
        setAnchorElUser(null);
        return;
      }
      setModalOpen(true);
      setsAnchorElUser(null);
      setAnchorElUser(null);
    } else if (option === "Theme") {
      setsAnchorElUser(event.currentTarget);
    }
  };

  const handleCloseUserMenu = () => {
    setsAnchorElUser(null);
    setAnchorElUser(null);
  };

  const handleClose = () => {
    setModalOpen(false);
    setUpdateStatus(null);
  };

  useEffect(() => {
    const getDatas = async () => {
      if (isSSO) {
        // console.log("SSO user detected - skipping user data fetch");
        const raw =
          localStorage.getItem("project_location") ||
          localStorage.getItem("location") ||
          localStorage.getItem("project") ||
          localStorage.getItem("operation") ||
          "";
        setProjectLocation(formatProjectLabel(raw));
        return;
      }

      if (!userId) {
        // console.log("No user_id found - skipping user data fetch");
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_IP}/api/user/get/${userId}`
        );
        const data = response.data;
        localStorage.setItem("email_address", data.email_address);
        localStorage.setItem("telephone", data.telephone);
        localStorage.setItem("project", data.operation);

        setUserDetails(data);

        setUserModal({
          name: data.user_name || localStorage.getItem("user_name") || "",
          id: userId,
          email: data.email_address || "",
        });

        const raw =
          localStorage.getItem("project_location") ||
          localStorage.getItem("location") ||
          localStorage.getItem("project") ||
          data.operation ||
          "";
        setProjectLocation(formatProjectLabel(raw));
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };
    getDatas();
  }, [isSSO, userId]);

  const updatedata = async () => {
    if (isSSO) {
      const message: any = (
        <Alert severity="warning">SSO users cannot update profile settings</Alert>
      );
      setUpdateIcon(message);
      const popupdiv = popup.current;
      if (popupdiv) {
        popupdiv.style.display = "block";
      }
      setTimeout(() => {
        if (popup.current) popup.current.style.display = "none";
      }, 2000);
      return;
    }

    if (
      userModal.name === " " ||
      userModal.name === "" ||
      userModal.name === null
    ) {
      const message: any = (
        <Alert severity="warning">Name field is required</Alert>
      );
      setUpdateIcon(message);
      const popupdiv = popup.current;
      if (popupdiv) {
        popupdiv.style.display = "block";
      }
      const popupdivNew = popup.current;
      setTimeout(() => {
        if (popupdivNew) popupdivNew.style.display = "none";
      }, 1000);
      return;
    }

    if (
      userModal.email === " " ||
      userModal.email === "" ||
      userModal.email === null
    ) {
      const message: any = (
        <Alert severity="warning">Email field is required</Alert>
      );
      setUpdateIcon(message);
      const popupdiv = popup.current;
      if (popupdiv) {
        popupdiv.style.display = "block";
      }
      const popupdivNew = popup.current;
      setTimeout(() => {
        if (popupdivNew) popupdivNew.style.display = "none";
      }, 1000);
      return;
    }

    const res = await axios.post(
      `${process.env.REACT_APP_BACKEND_IP}/api/user/update/${userId}`,
      userModal,
      {
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          Authorization: "Basic " + btoa("admin:admin"),
        },
      }
    );

    if (res.status === 200) {
      const message: any = (
        <Alert severity="success">Successfully updated.</Alert>
      );
      setUpdateIcon(message);
      const popupdiv = popup.current;
      if (popupdiv) {
        popupdiv.style.display = "block";
      }
      const popupdivNew = popup.current;
      setTimeout(() => {
        if (popupdivNew) popupdivNew.style.display = "none";
      }, 1000);
    } else {
      const message: any = (
        <Alert severity="error">Something went wrong.</Alert>
      );
      setUpdateIcon(message);
      const popupdiv = popup.current;
      if (popupdiv) {
        popupdiv.style.display = "block";
      }
      const popupdivNew = popup.current;
      setTimeout(() => {
        if (popupdivNew) popupdivNew.style.display = "none";
      }, 1000);
    }
  };

  const updatepassword = async () => {
    if (isSSO) {
      const message: any = (
        <Alert severity="warning">SSO users cannot change password here</Alert>
      );
      setUpdateIcon(message);
      const popupdiv = popup.current;
      if (popupdiv) {
        popupdiv.style.display = "block";
      }
      setTimeout(() => {
        if (popup.current) popup.current.style.display = "none";
      }, 2000);
      return;
    }

    if (
      passwordModal.old_password === " " ||
      passwordModal.old_password === "" ||
      passwordModal.old_password === null
    ) {
      const message: any = (
        <Alert severity="warning"> Current password field is required. </Alert>
      );
      setUpdateIcon(message);
      const popupdiv = popup.current;
      if (popupdiv) {
        popupdiv.style.display = "block";
      }
      const popupdivNew = popup.current;
      setTimeout(() => {
        if (popupdivNew) popupdivNew.style.display = "none";
      }, 1000);
      return;
    }

    if (
      passwordModal.password === " " ||
      passwordModal.password === "" ||
      passwordModal.password === null
    ) {
      const popupdiv = popup.current;
      const message: any = (
        <Alert severity="warning"> New password field is required. </Alert>
      );
      setUpdateIcon(message);
      if (popupdiv) {
        popupdiv.style.display = "block";
      }
      const popupdivNew = popup.current;
      setTimeout(() => {
        if (popupdivNew) popupdivNew.style.display = "none";
      }, 1000);
      return;
    }

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_IP}/api/user/update/${userId}`,
        passwordModal,
        {
          headers: {
            "Content-type": "application/json; charset=UTF-8",
            Authorization: "Basic " + btoa("admin:admin"),
          },
        }
      );

      if (res.status === 200) {
        const message: any = (
          <Alert severity="success"> Successfully updated </Alert>
        );
        setUpdateIcon(message);
        const popupdiv = popup.current;
        if (popupdiv) {
          popupdiv.style.display = "block";
        }
        setTimeout(() => {
          localStorage.clear();
          sessionStorage.clear();
          sessionStorage.removeItem("token");
          localStorage.removeItem("login");
          localStorage.removeItem("user_id");
          localStorage.removeItem("user_name");
          localStorage.removeItem("email_address");
          localStorage.removeItem("telephone");
          localStorage.removeItem("project");
          sessionStorage.setItem("loginStep", "1");
          window.location.replace("/auth");
        }, 1500);
      } else if (res.status === 401) {
        const message: any = (
          <Alert severity="error"> Invalid Password. </Alert>
        );
        setUpdateIcon(message);
        const popupdiv = popup.current;
        if (popupdiv) {
          popupdiv.style.display = "block";
        }
        const popupdivNew = popup.current;
        setTimeout(() => {
          if (popupdivNew) popupdivNew.style.display = "none";
        }, 1000);
      } else {
        const message: any = (
          <Alert severity="error"> Something went wrong. </Alert>
        );
        setUpdateIcon(message);
        const popupdiv = popup.current;
        if (popupdiv) {
          popupdiv.style.display = "block";
        }
        setTimeout(() => {
          if (popup.current) popup.current.style.display = "none";
        }, 1000);
      }
    } catch (error: any) {
      console.error("Error updating password:", error);

      if (error.response?.status === 401) {
        const message: any = (
          <Alert severity="error"> Invalid Password. </Alert>
        );
        setUpdateIcon(message);
        const popupdiv = popup.current;
        if (popupdiv) {
          popupdiv.style.display = "block";
        }
        const popupdivNew = popup.current;
        setTimeout(() => {
          if (popupdivNew) popupdivNew.style.display = "none";
        }, 1000);
      } else {
        const message: any = (
          <Alert severity="error"> Server error. Please try again. </Alert>
        );
        setUpdateIcon(message);
        const popupdiv = popup.current;
        if (popupdiv) {
          popupdiv.style.display = "block";
        }
        setTimeout(() => {
          if (popup.current) popup.current.style.display = "none";
        }, 1000);
      }
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("auth_operation");
    localStorage.removeItem("login");
    localStorage.removeItem("user_id");
    localStorage.removeItem("user_name");
    localStorage.removeItem("email_address");
    localStorage.removeItem("telephone");
    localStorage.removeItem("project");
    localStorage.removeItem("sso_multi_client");
    localStorage.removeItem("sso_client_ids");
    localStorage.removeItem("sso_client_names");
    localStorage.removeItem("fid");
    localStorage.removeItem("operation");
    sessionStorage.setItem("loginStep", "1");
    window.location.replace("/auth");
  };

  const [themeSwitch, setThemeSwitch] = useState(
    localStorage.getItem("theme") === "dark"
  );

  const [userEdit, setUserEdit] = useState({
    name: true,
    project: true,
    email: true,
    telephone: true,
  });

  const [userDetails, setUserDetails] = useState(Object());

  function disablePopup() {
    const popupdiv = popup.current;
    if (popupdiv) {
      popupdiv.style.display = "none";
    }
  }
return (
    <>
      <div className={props.className}>
        <div className="topbar">
          <div className="topbar-hold fixed">
            <div className="flex flex-space-between flex-middle h-100">
              <div className="flex flex-middle">
                <IconButton onClick={handleToggle}>
                  <MenuIcon style={{ color: "white" }} />
                </IconButton>

                <div style={mystyle.projectLocation}>
                  <LocationOnIcon style={{ fontSize: "18px" }} />
                  <Typography variant="body1" component="span">
                    {projectLocation}
                  </Typography>
                </div>
              </div>

              <div className="flex flex-middle" style={{ marginRight: "2em" }}>
                <Box
                  sx={{ flexGrow: 0 }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginRight: "0px",
                  }}
                >
                  <button
                    onClick={(e) => handleOpenUserMenu(e)}
                    className="settings-trigger-btn"
                  >
                    <SettingsIcon />
                  </button>

                  <Menu
                    sx={{ mt: "12px" }}
                    id="menu-appbar"
                    anchorEl={anchorElUser}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    keepMounted
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    open={Boolean(anchorElUser)}
                    onClose={handleCloseUserMenu}
                    PaperProps={{
                      sx: {
                        borderRadius: "16px",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                        border: "1px solid #e8eaf0",
                        overflow: "hidden",
                        minWidth: "230px",
                        p: 0,
                      },
                    }}
                  >
                    {!isSSO && (
                      <MenuItem
                        className="settings-menu-item"
                        onClick={(e: any) => handleOpenUserMenus(e, "Settings")}
                      >
                        <span className="menu-item-icon">
                          <AccountCircleIcon />
                        </span>
                        <span className="menu-item-label">Personal Details</span>
                      </MenuItem>
                    )}

                    <div className="settings-menu-divider" />

                    <MenuItem
                      className="settings-menu-item menu-item-logout"
                      onClick={handleLogout}
                      sx={{ mb: "4px" }}
                    >
                      <span className="menu-item-icon">
                        <LogoutIcon />
                      </span>
                      <span className="menu-item-label">Logout</span>
                    </MenuItem>
                  </Menu>
                </Box>
              </div>
            </div>
          </div>
        </div>

        <Dialog
          style={{ width: "100vw" }}
          open={modalOpen}
          onClose={handleClose}
          PaperProps={{
            style: {
              borderRadius: "24px",
              overflowY: "auto",
              width: "460px",
              maxWidth: "95vw",
              maxHeight: "90vh",
              boxShadow: "0 8px 40px rgba(0,0,0,0.14)",
            },
          }}
        >
          <div className="settings_div">
            <div className="settings_profile_ico_details_div">

              <div className="settings_details_inner">
                <span className="settings_section_title">Personal Details</span>

                <div className="settings_input">
                  <input
                    value={userModal.name}
                    onChange={(event) =>
                      setUserModal({ ...userModal, name: event.currentTarget.value })
                    }
                    placeholder="Full Name"
                    disabled={isSSO}
                  />
                  <span className="settings_input_icon">
                    <AccountCircleIcon />
                  </span>
                </div>

                <div className="settings_input">
                  <input
                    value={userModal.email}
                    onChange={(event) =>
                      setUserModal({ ...userModal, email: event.currentTarget.value })
                    }
                    placeholder="Email Address"
                    disabled={isSSO}
                  />
                  <span className="settings_input_icon">
                    <EmailOutlined />
                  </span>
                </div>

                <div className="settings_buttons">
                  <button
                    className="btn-cancel"
                    onClick={() => {
                      setModalOpen(false);
                      setsAnchorElUser(null);
                      setAnchorElUser(null);
                    }}
                  >
                    Cancel
                  </button>
                  {!isSSO && (
                    <button className="btn-save" onClick={updatedata}>
                      Save Changes
                    </button>
                  )}
                </div>
              </div>

              <div className="settings_divider" />

              <div className="settings_details_inner">
                <span className="settings_section_title">Security</span>

                <div className="settings_input">
                  <input
                    type={showOldPassword ? "text" : "password"}
                    value={passwordModal.old_password}
                    onChange={(event) =>
                      setPasswordModal({ ...passwordModal, old_password: event.currentTarget.value })
                    }
                    placeholder="Current Password"
                    disabled={isSSO}
                  />
                  <span
                    className="settings_input_icon clickable"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setShowOldPassword(!showOldPassword);
                    }}
                  >
                    {showOldPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </span>
                </div>

                <div className="settings_input">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={passwordModal.password}
                    onChange={(event) =>
                      setPasswordModal({ ...passwordModal, password: event.currentTarget.value })
                    }
                    placeholder="New Password"
                    disabled={isSSO}
                  />
                  <span
                    className="settings_input_icon clickable"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setShowNewPassword(!showNewPassword);
                    }}
                  >
                    {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </span>
                </div>

                <div className="settings_buttons">
                  <button
                    className="btn-cancel"
                    onClick={() => {
                      setModalOpen(false);
                      setsAnchorElUser(null);
                      setAnchorElUser(null);
                    }}
                  >
                    Cancel
                  </button>
                  {!isSSO && (
                    <button className="btn-save" onClick={updatepassword}>
                      Update Password
                    </button>
                  )}
                </div>
              </div>

              {updateStatus !== null ? (
                <div style={{ width: "100%" }}>
                  <h4
                    style={{
                      background: `${
                        updateStatus !== "Invalid Password" ||
                        updateStatus !== "Something went wrong"
                          ? "green"
                          : "red"
                      }`,
                      color: "white",
                      padding: "10px",
                      marginLeft: "20px",
                      textAlign: "center",
                      marginRight: "20px",
                      borderRadius: "5px",
                      fontWeight: "bold",
                    }}
                  >
                    {updateStatus}
                  </h4>
                </div>
              ) : null}
            </div>
          </div>

          <div ref={popup} className="popup">
            <div className="popup-div">
              <div
                className="popup-content"
                style={{ background: "transparent", border: "0" }}
              >
                <div>
                  <div className="success-icon">{updateIcon}</div>
                </div>
              </div>
            </div>
          </div>
        </Dialog>
      </div>
    </>
  );
};

export default Header;